import React, { useEffect } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useFeatureFlagContext } from '../contexts/FeatureFlagContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faUserCheck, faGem } from '@/icons';
import { SignupProgress, USER_SIGNUP_STEPS } from '../components/SignupProgress';
import { clearSignupSession, useSignupSession } from '../contexts/SignupSessionContext';

export const RegisterCompletionPage: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { flags, isLoading: flagsLoading } = useFeatureFlagContext();
    const selfServeSignup = flags['SelfServiceSignup'] ?? false;
    const { session } = useSignupSession();
    const { state } = useLocation();
    const plan = (state as { plan?: string } | null)?.plan ?? '';

    useEffect(() => {
        document.title = 'Welcome to Calculist!';
        // Clear session from localStorage on mount
        clearSignupSession();
    }, []);

    if (flagsLoading) return null;
    if (!selfServeSignup) return <Navigate to="/auth/login" replace />;

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            backgroundColor: theme.colors.background,
            padding: theme.spacing.md,
            paddingTop: theme.spacing.xl,
        }}>
            <div style={{ ...themedStyles.card.default, maxWidth: '560px', width: '100%' }}>
                <SignupProgress currentStep={3} steps={USER_SIGNUP_STEPS} />

                {/* Success header */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: theme.spacing.xl }}>
                    <img src="/logo-name.svg" alt="Calculist" style={{ height: '48px', width: 'auto', marginBottom: theme.spacing.lg }} />
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '72px',
                        height: '72px',
                        borderRadius: '50%',
                        backgroundColor: '#f0fdf4',
                        color: '#16a34a',
                        fontSize: '32px',
                        marginBottom: theme.spacing.md,
                    }}>
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <h1 style={{ ...themedStyles.text.heading, marginTop: 0, marginBottom: theme.spacing.xs }}>You're all set!</h1>
                    <p style={{ ...themedStyles.text.muted, marginTop: 0 }}>
                        Your account has been created. You can now sign in to Calculist.
                    </p>
                </div>

                {(session.teamName || session.members.length > 0) && (
                    <div style={{
                        backgroundColor: theme.colors.surfaceSecondary,
                        border: `1px solid ${theme.colors.border.default}`,
                        borderRadius: theme.borderRadius.lg,
                        padding: theme.spacing.md,
                        marginBottom: theme.spacing.xl,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: theme.spacing.md,
                    }}>
                        {session.teamName && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ ...themedStyles.text.muted, fontSize: theme.typography.fontSize.sm }}>Team</span>
                                <span style={{ fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary }}>
                                    {session.teamName}
                                </span>
                            </div>
                        )}

                        {session.members.length > 0 && (
                            <div style={{ borderTop: session.teamName ? `1px solid ${theme.colors.border.default}` : undefined, paddingTop: session.teamName ? theme.spacing.md : undefined }}>
                                <div style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.semibold, color: theme.colors.text.primary, marginBottom: theme.spacing.sm }}>
                                    Team members invited
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                                    {session.members.map((m, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing.sm }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xs }}>
                                                <FontAwesomeIcon icon={faUserCheck} style={{ color: '#16a34a', fontSize: '13px', flexShrink: 0 }} />
                                                <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.primary }}>
                                                    {[m.firstName, m.lastName].filter(Boolean).join(' ') || m.email}
                                                </span>
                                            </div>
                                            <span style={{
                                                fontSize: theme.typography.fontSize.xs,
                                                fontWeight: theme.typography.fontWeight.semibold,
                                                color: '#16a34a',
                                                backgroundColor: '#f0fdf4',
                                                padding: '2px 8px',
                                                borderRadius: '999px',
                                                border: '1px solid #bbf7d0',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                Invitation sent
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {plan === 'pro' && (
                    <div style={{
                        backgroundColor: '#faf5ff',
                        border: '1px solid #e9d5ff',
                        borderRadius: theme.borderRadius.lg,
                        padding: theme.spacing.md,
                        marginBottom: theme.spacing.md,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, marginBottom: theme.spacing.xs }}>
                            <FontAwesomeIcon icon={faGem} style={{ color: '#7c3aed', fontSize: '18px', flexShrink: 0 }} />
                            <span style={{ fontSize: theme.typography.fontSize.md, fontWeight: theme.typography.fontWeight.bold, color: '#6b21a8' }}>You're on the Pro plan</span>
                        </div>
                        <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, color: '#6b21a8' }}>
                            Look for the <FontAwesomeIcon icon={faGem} style={{ fontSize: '12px' }} /> icon throughout Calculist — it marks useful features exclusive to your Pro plan.
                        </p>
                    </div>
                )}

                {plan === 'basic' && (
                    <div style={{
                        backgroundColor: theme.colors.surfaceSecondary,
                        border: `1px solid ${theme.colors.border.default}`,
                        borderRadius: theme.borderRadius.lg,
                        padding: theme.spacing.md,
                        marginBottom: theme.spacing.md,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, marginBottom: theme.spacing.xs }}>
                            <FontAwesomeIcon icon={faGem} style={{ color: theme.colors.text.secondary, fontSize: '16px', flexShrink: 0 }} />
                            <span style={{ fontSize: theme.typography.fontSize.md, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary }}>Want more features?</span>
                        </div>
                        <p style={{ margin: 0, fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                            You're on the Basic plan. Whenever you see the <FontAwesomeIcon icon={faGem} style={{ fontSize: '12px' }} /> icon on a feature, click it to upgrade to Pro and unlock it.
                        </p>

                    </div>
                )}

                <button
                    type="button"
                    onClick={() => navigate('/auth/login')}
                    style={{
                        ...themedStyles.button.primary,
                        width: '100%',
                        padding: `${theme.spacing.md} ${theme.spacing.xl}`,
                        textAlign: 'center',
                    }}
                >
                    Sign In to Calculist
                </button>
            </div>
        </div>
    );
};
